// scoring-logic.js
// Pure functions for match state operations and analytics

export function runRate(runs, balls) { return balls === 0 ? "—" : ((runs / balls) * 6).toFixed(2); }
export function reqRate(needRuns, ballsLeft) { return ballsLeft <= 0 ? "—" : ((needRuns / ballsLeft) * 6).toFixed(2); }

// ─── MATCH PHASE DETECTION ──────────────────────────────────────────────
// PowerPlay / Middle Overs / Death Overs badge logic.
// Custom rules per user: 8-over → PP 1-2, Mid 3-6, Death 7-8
//                        10-over → PP 1-3, Mid 4-8, Death 9-10
export function phaseBoundaries(totalOvers) {
  if (totalOvers === 8) return { ppEnd: 2, deathStart: 7 };
  if (totalOvers === 10) return { ppEnd: 3, deathStart: 9 };
  if (totalOvers <= 6) return { ppEnd: 1, deathStart: totalOvers }; // very short, no death
  const quarter = Math.ceil(totalOvers * 0.25);
  return { ppEnd: quarter, deathStart: totalOvers - quarter + 1 };
}

export function getMatchPhase(totalOvers, legalBalls) {
  const currentOver = Math.floor(legalBalls / 6) + 1; // 1-indexed human over
  if (legalBalls === 0) return { name: "POWERPLAY", color: "#2563eb", icon: "⚡" };
  const { ppEnd, deathStart } = phaseBoundaries(totalOvers);
  if (currentOver <= ppEnd) return { name: "POWERPLAY", color: "#2563eb", icon: "⚡" };
  if (currentOver >= deathStart) return { name: "DEATH OVERS", color: "#dc2626", icon: "🔥" };
  return { name: "MIDDLE OVERS", color: "#7c3aed", icon: "🏏" };
}

export function getPhaseStats(inn, totalOvers) {
  let ppRuns = 0, ppWickets = 0, ppBalls = 0;
  let midRuns = 0, midWickets = 0, midBalls = 0;
  let dthRuns = 0, dthWickets = 0, dthBalls = 0;
  const { ppEnd, deathStart } = phaseBoundaries(totalOvers);

  inn.ballLog.forEach(b => {
    const currentOver = b.over + 1;
    let isWicket = b.type === "wicket" ? 1 : 0;
    if (currentOver <= ppEnd) {
      ppRuns += b.runs; ppWickets += isWicket; if (b.isLegal) ppBalls++;
    } else if (currentOver >= deathStart) {
      dthRuns += b.runs; dthWickets += isWicket; if (b.isLegal) dthBalls++;
    } else {
      midRuns += b.runs; midWickets += isWicket; if (b.isLegal) midBalls++;
    }
  });

  const getRR = (runs, balls) => balls === 0 ? "0.0" : ((runs / balls) * 6).toFixed(1);

  return [
    { label: "POWERPLAY", runs: ppRuns, wickets: ppWickets, overs: formatOvers(ppBalls), rr: getRR(ppRuns, ppBalls), color: "#2563eb" },
    { label: "MIDDLE OVERS", runs: midRuns, wickets: midWickets, overs: formatOvers(midBalls), rr: getRR(midRuns, midBalls), color: "#7c3aed" },
    { label: "DEATH OVERS", runs: dthRuns, wickets: dthWickets, overs: formatOvers(dthBalls), rr: getRR(dthRuns, dthBalls), color: "#dc2626" }
  ];
}

// ─── DOT BALL PRESSURE ──────────────────────────────────────────────────
export function getConsecutiveDots(inn) {
  let count = 0;
  for (let i = inn.ballLog.length - 1; i >= 0; i--) {
    const b = inn.ballLog[i];
    if (b.type === "dot" && b.isLegal) count++;
    else break;
  }
  return count;
}

// ─── MOMENTUM METER ─────────────────────────────────────────────────────
// Compares last 12 legal balls run rate with overall run rate.
export function getMomentumData(inn) {
  if (inn.balls < 6) return null; // need at least 1 over
  const last12 = [];
  let counted = 0;
  for (let i = inn.ballLog.length - 1; i >= 0 && counted < 12; i--) {
    last12.push(inn.ballLog[i]);
    if (inn.ballLog[i].isLegal) counted++;
  }
  const recentRuns = last12.reduce((s, b) => s + b.runs, 0);
  const recentLegal = last12.filter(b => b.isLegal).length;
  const recentRR = recentLegal > 0 ? (recentRuns / recentLegal) * 6 : 0;
  const overallRR = inn.balls > 0 ? (inn.totalRuns / inn.balls) * 6 : 0;
  // ratio > 1 = accelerating, < 1 = slowing
  const ratio = overallRR > 0 ? recentRR / overallRR : 1;
  return { recentRR: recentRR.toFixed(1), overallRR: overallRR.toFixed(1), ratio };
}

// ─── LAST 5 OVERS RUN RATE ─────────────────────────────────────────────
export function getLast5OversRR(inn) {
  const completedOvers = Math.floor(inn.balls / 6);
  if (completedOvers < 1) return null;
  // Include the over currently in progress — excluding it made the card show a
  // stale window (at 19.5 it reported overs 14-18 and ignored the 20th).
  const currentOver = Math.floor(inn.balls / 6) + (inn.balls % 6 === 0 ? 0 : 1);
  const startOver = Math.max(0, currentOver - 5);
  let runs = 0, legal = 0;
  for (const b of inn.ballLog) {
    if (b.over >= startOver) {
      runs += b.runs;
      if (b.isLegal) legal++;
    }
  }
  return { runs, overs: formatOvers(legal), balls: legal, rr: legal > 0 ? ((runs / legal) * 6).toFixed(1) : "0.0" };
}

// ─── FALL OF WICKETS ────────────────────────────────────────────────────
export function getFallOfWickets(inn) {
  const fow = [];
  let runningScore = 0;
  let legalBalls = 0; // running counter — the old slice().filter() per wicket was O(N²)
  for (let i = 0; i < inn.ballLog.length; i++) {
    const b = inn.ballLog[i];
    runningScore += b.runs;
    if (b.isLegal) legalBalls++;
    if (b.type === "wicket") {
      fow.push({
        wicket: fow.length + 1,
        score: runningScore,
        overs: formatOvers(legalBalls),
        batsman: b.outBatsman || b.striker || null
      });
    }
  }
  return fow;
}

// ─── WIN PREDICTOR (chase only) ─────────────────────────────────────────
// Lightweight WinViz-style heuristic: compares the required rate against an
// "achievable" rate that scales with wickets in hand, through a logistic
// curve. Not DLS — just broadcast-style drama with sensible numbers.
export function computeChaseWinPct(m) {
  if (!m || m.innings.length < 2) return null;
  const inn = currentInnings(m);
  const target = m.innings[0].totalRuns + 1;
  const need = target - inn.totalRuns;
  const ballsLeft = m.overs * 6 - inn.balls;
  const battingPlayers = inn.battingTeam === "A" ? m.teamA.players : m.teamB.players;
  const maxWkts = m.lms ? battingPlayers.length : battingPlayers.length - 1;
  const wktsLeft = Math.max(0, maxWkts - inn.wickets);
  if (need <= 0) return 100;
  if (ballsLeft <= 0 || wktsLeft <= 0) return 0;
  const rrr = (need / ballsLeft) * 6;
  const achievable = 3 + 9 * Math.min(1, wktsLeft / 5); // 3–12 rpo band by wickets in hand
  const p = 1 / (1 + Math.exp((rrr - achievable) / 2.0));
  return Math.max(1, Math.min(99, Math.round(p * 100)));
}

// ─── MAIDEN OVER CHECK ──────────────────────────────────────────────────
export function isMaidenOver(inn, overIdx) {
  const balls = inn.ballLog.filter(b => b.over === overIdx);
  if (balls.length === 0) return false;
  const legalBalls = balls.filter(b => b.isLegal);
  if (legalBalls.length < 6) return false;
  // Wides/no-balls are charged to the bowler and break the maiden; byes and
  // leg-byes are NOT charged to the bowler and do not break it (Law 98.3).
  if (balls.some(b => b.type === "wide" || b.type === "nb")) return false;
  const bowlerRuns = balls.reduce((s, b) => {
    if (b.type === "b" || b.type === "lb") return s;
    if (b.runSource === "b" || b.runSource === "lb") return s;
    return s + b.runs;
  }, 0);
  return bowlerRuns === 0;
}

export function _snapshot(match) {
  if (!match._undoStack) match._undoStack = [];
  const stack = match._undoStack;
  match._undoStack = undefined; // hide from clone
  const snap = JSON.parse(JSON.stringify(match));
  match._undoStack = stack;
  stack.push(snap);
  if (stack.length > 12) stack.shift();
}
export function undoLastBall(match) {
  const stack = match._undoStack;
  if (!stack || stack.length === 0) return;
  const snap = stack.pop();
  const localSession = {
    _deviceRole: match._deviceRole,
    _peerRoomCode: match._peerRoomCode,
    _introPlayedAt: match._introPlayedAt,
    // Keep revision/save markers monotonic: restoring the snapshot's older
    // values would make persisted/peer snapshots look newer than the undone
    // state, resurrecting the undone ball after a reload or on the recorder.
    _stateRevision: match._stateRevision,
    _savedAt: match._savedAt,
    _cloudCode: match._cloudCode,
    _cloudEditToken: match._cloudEditToken
  };
  // Preserve the (mutable) stack reference; restore everything else
  Object.keys(match).forEach(k => { if (k !== "_undoStack") delete match[k]; });
  Object.assign(match, snap);
  match._undoStack = stack; // re-attach (snap doesn't have it because we hid it before clone)
  for (const [key, value] of Object.entries(localSession)) {
    if (value !== undefined) match[key] = value;
  }
}
export function _resetUndo(match) {
  if (match) match._undoStack = [];
}

export function createMatch(opts) {
  const { teamA, teamB, overs, mode = "full", lms, toss, challenge, rules, tournamentId, tournamentMatchId, cloudTournamentId, cloudFixtureId } = opts;
  const battingTeam = (toss.choice === "bat") ? toss.winner : (toss.winner === "A" ? "B" : "A");
  const battingPlayers = battingTeam === "A" ? teamA.players : teamB.players;
  const bowlingPlayers = battingTeam === "A" ? teamB.players : teamA.players;
  return {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    teamA, teamB, overs, mode, lms, toss, challenge,
    tournamentId, tournamentMatchId,
    cloudTournamentId, cloudFixtureId,
    rules: rules || {
      maxOversPerBowler: Math.ceil(overs / 5),
      wideValue: 1,
      noBallValue: 1,
      rebowlWides: true,
      rebowlNoBalls: true
    },
    innings: [makeInnings(battingTeam, battingPlayers, bowlingPlayers)],
    result: null
  };
}

export function currentInnings(match) { return match.innings[match.innings.length - 1]; }

export function setOpeners(match, strikerName, nonStrikerName, bowlerName) {
  const inn = currentInnings(match);
  inn.currentStriker = strikerName;
  inn.currentNonStriker = nonStrikerName;
  inn.currentBowler = bowlerName;
}

export function _getBatsman(inn, name) { return inn.batsmen.find(b => b.name === name); }
export function _getBowler(inn, name) { return inn.bowlers.find(b => b.name === name); }

export function recordRuns(match, runs) {
  const inn0 = currentInnings(match);
  // Defensive: never mutate without a striker/bowler on strike — the UI's
  // `ready` state normally prevents this, but a peer-sync or recovery edge
  // could call in with an empty slot; crashing here would corrupt the innings.
  if (!_getBatsman(inn0, inn0.currentStriker) || !_getBowler(inn0, inn0.currentBowler)) return;
  _snapshot(match);
  const inn = currentInnings(match);
  const striker = _getBatsman(inn, inn.currentStriker);
  const bowler = _getBowler(inn, inn.currentBowler);

  inn.totalRuns += runs;
  inn.balls += 1;
  striker.balls += 1;
  striker.runs += runs;
  if (runs === 4) striker.fours += 1;
  if (runs === 6) striker.sixes += 1;
  bowler.balls += 1;
  bowler.runs += runs;

  inn.ballLog.push({ over: Math.floor((inn.balls - 1) / 6), ball: ((inn.balls - 1) % 6) + 1, runs, type: runs === 0 ? "dot" : "run", striker: striker.name, bowler: bowler.name, isLegal: true, freeHit: inn.freeHit });
  inn.freeHit = false;

  if (runs % 2 === 1) _swapStrikers(inn);
}

export function _swapStrikers(inn) {
  // Special case: striker was nulled by recordWicket. Don't swap nulls — instead
  // promote non-striker to striker so the surviving batter faces (correct after
  // an end-of-over wicket). New batter comes in via setNewBatsman as nonStriker.
  if (inn.currentStriker === null) {
    inn.currentStriker = inn.currentNonStriker;
    inn.currentNonStriker = null;
    return;
  }
  const tmp = inn.currentStriker;
  inn.currentStriker = inn.currentNonStriker;
  inn.currentNonStriker = tmp;
}

export function recordWide(match, additionalRuns) {
  const inn0 = currentInnings(match);
  if (!_getBowler(inn0, inn0.currentBowler)) return; // no bowler set — ignore
  _snapshot(match);
  const inn = currentInnings(match);
  const bowler = _getBowler(inn, inn.currentBowler);
  const rules = match.rules || {};
  const wideVal = rules.wideValue !== undefined ? rules.wideValue : 1;
  const rebowl = rules.rebowlWides !== false;
  const total = wideVal + additionalRuns;
  inn.totalRuns += total;
  inn.extras.wide += total;
  bowler.runs += total;

  if (!rebowl) {
    inn.balls += 1;
    bowler.balls += 1;
  }

  inn.ballLog.push({
    over: Math.floor((inn.balls - (rebowl ? 0 : 1)) / 6),
    ball: ((inn.balls - (rebowl ? 0 : 1)) % 6) + 1,
    runs: total,
    type: "wide",
    striker: inn.currentStriker,
    bowler: bowler.name,
    isLegal: !rebowl,
    freeHit: inn.freeHit
  });

  if (!rebowl) {
    inn.freeHit = false;
  }

  if (additionalRuns % 2 === 1) _swapStrikers(inn);
}

export function recordNoBall(match, batRuns) {
  const inn0 = currentInnings(match);
  if (!_getBatsman(inn0, inn0.currentStriker) || !_getBowler(inn0, inn0.currentBowler)) return;
  _snapshot(match);
  const inn = currentInnings(match);
  const striker = _getBatsman(inn, inn.currentStriker);
  const bowler = _getBowler(inn, inn.currentBowler);
  const rules = match.rules || {};
  const nbVal = rules.noBallValue !== undefined ? rules.noBallValue : 1;
  const rebowl = rules.rebowlNoBalls !== false;
  
  inn.totalRuns += nbVal + batRuns;
  inn.extras.nb += nbVal;
  bowler.runs += nbVal + batRuns;
  striker.runs += batRuns;
  striker.balls += 1; // a no-ball counts as a ball faced (unlike a wide)
  if (batRuns === 4) striker.fours += 1;
  if (batRuns === 6) striker.sixes += 1;

  if (!rebowl) {
    inn.balls += 1;
    bowler.balls += 1;
  }

  inn.ballLog.push({
    over: Math.floor((inn.balls - (rebowl ? 0 : 1)) / 6),
    ball: ((inn.balls - (rebowl ? 0 : 1)) % 6) + 1,
    runs: nbVal + batRuns,
    type: "nb",
    striker: striker.name,
    bowler: bowler.name,
    isLegal: !rebowl,
    freeHit: inn.freeHit
  });

  if (rebowl) {
    inn.freeHit = true;
  } else {
    inn.freeHit = false;
  }

  if (batRuns % 2 === 1) _swapStrikers(inn);
}

export function recordNoBallExtra(match, runs, runSource) {
  const inn0 = currentInnings(match);
  // A no-ball extra still needs both a striker (it counts as a ball faced) and a
  // bowler. Without the striker check this logs `null` strikers into the ballLog
  // when called mid-transition from peer sync / recovery.
  if (!_getBatsman(inn0, inn0.currentStriker) || !_getBowler(inn0, inn0.currentBowler)) return;
  _snapshot(match);
  const inn = currentInnings(match);
  const striker = _getBatsman(inn, inn.currentStriker);
  const bowler = _getBowler(inn, inn.currentBowler);
  const source = runSource === "legbye" || runSource === "lb" ? "lb" : "b";
  const rules = match.rules || {};
  const nbVal = rules.noBallValue !== undefined ? rules.noBallValue : 1;
  const rebowl = rules.rebowlNoBalls !== false;
  const total = nbVal + runs;

  inn.totalRuns += total;
  // ICC Law 21.13: runs that do not come off the bat on a No Ball are scored as
  // No Ball extras — never as byes/leg-byes — and the bowler is charged for ALL
  // of them. `runSource` is still recorded on the ballLog entry for commentary.
  inn.extras.nb += total;
  bowler.runs += total;
  if (striker) striker.balls += 1; // a no-ball counts as a ball faced

  if (!rebowl) {
    inn.balls += 1;
    bowler.balls += 1;
  }

  inn.ballLog.push({
    over: Math.floor((inn.balls - (rebowl ? 0 : 1)) / 6),
    ball: ((inn.balls - (rebowl ? 0 : 1)) % 6) + 1,
    runs: total,
    type: "nb",
    runSource: source,
    striker: inn.currentStriker,
    bowler: bowler.name,
    isLegal: !rebowl,
    freeHit: inn.freeHit
  });

  if (rebowl) {
    inn.freeHit = true;
  } else {
    inn.freeHit = false;
  }

  if (runs % 2 === 1) _swapStrikers(inn);
}

export function recordBye(match, runs) { _recordByeOrLb(match, runs, "b"); }
export function recordLegBye(match, runs) { _recordByeOrLb(match, runs, "lb"); }

export function _recordByeOrLb(match, runs, kind) {
  const inn0 = currentInnings(match);
  if (!_getBatsman(inn0, inn0.currentStriker) || !_getBowler(inn0, inn0.currentBowler)) return;
  _snapshot(match);
  const inn = currentInnings(match);
  const striker = _getBatsman(inn, inn.currentStriker);
  const bowler = _getBowler(inn, inn.currentBowler);
  inn.totalRuns += runs;
  inn.extras[kind] += runs;
  inn.balls += 1;
  striker.balls += 1;
  bowler.balls += 1;
  inn.ballLog.push({ over: Math.floor((inn.balls - 1) / 6), ball: ((inn.balls - 1) % 6) + 1, runs, type: kind, striker: striker.name, bowler: bowler.name, isLegal: true, freeHit: inn.freeHit });
  inn.freeHit = false;
  if (runs % 2 === 1) _swapStrikers(inn);
}

export function recordWicket(match, opts) {
  const inn0 = currentInnings(match);
  // Defensive: a wicket needs a striker on strike and a bowler — without a
  // striker this would crash (`striker.runs`). The UI guards this, but peer
  // sync / recovery could call in mid-transition; ignore instead of crashing.
  if (!_getBatsman(inn0, inn0.currentStriker) || !_getBowler(inn0, inn0.currentBowler)) return;
  _snapshot(match);
  const inn = currentInnings(match);

  if (inn.freeHit) {
    const legalOnFreeHit = ['Run Out', 'Obstructing the Field', 'Timed Out', 'Retired', 'Retired Hurt'];
    if (!legalOnFreeHit.includes(opts.type)) {
      return; // Dismiss invalid types silently on Free Hit
    }
  }

  const striker = _getBatsman(inn, inn.currentStriker);
  const bowler = _getBowler(inn, inn.currentBowler);

  const isLegal = opts.isLegal !== undefined ? opts.isLegal : true;
  const extraType = opts.extraType || null; // "wide", "nb"
  const completedRuns = Number(opts.runsCompleted ?? opts.extraRuns ?? 0) || 0;
  const wasFreeHit = !!inn.freeHit;
  const strikerBefore = inn.currentStriker;
  const nonStrikerBefore = inn.currentNonStriker;
  
  let batRunsAdded = 0;
  let totalAdded = 0;
  let bowlerRunsAdded = 0;
  const rules = match.rules || {};
  const wideVal = rules.wideValue !== undefined ? rules.wideValue : 1;
  const nbVal = rules.noBallValue !== undefined ? rules.noBallValue : 1;
  const rebowlWide = rules.rebowlWides !== false;
  const rebowlNoBall = rules.rebowlNoBalls !== false;

  if (extraType === "wide") {
    totalAdded += wideVal + completedRuns;
    inn.extras.wide += wideVal + completedRuns;
    bowlerRunsAdded += wideVal + completedRuns;
  } else if (extraType === "nb") {
    totalAdded += nbVal + completedRuns;
    if (opts.runSource === "bye" || opts.runSource === "legbye") {
      // ICC Law 21.13: non-bat runs off a No Ball are No Ball extras, not
      // byes/leg-byes, and the bowler is charged for all of them.
      inn.extras.nb += nbVal + completedRuns;
      bowlerRunsAdded += nbVal + completedRuns;
    } else {
      inn.extras.nb += nbVal;
      batRunsAdded += completedRuns;
      bowlerRunsAdded += nbVal + completedRuns;
    }
    if (rebowlNoBall) inn.freeHit = true;
  } else if (opts.runSource === "bye" || opts.runSource === "legbye") {
    // Run out while attempting byes/leg-byes on a legal delivery: the completed
    // runs are extras — not striker runs, not charged to the bowler.
    const extrasKey = opts.runSource === "bye" ? "b" : "lb";
    inn.extras[extrasKey] += completedRuns;
    totalAdded += completedRuns;
  } else {
    batRunsAdded += completedRuns;
    totalAdded += batRunsAdded;
    bowlerRunsAdded += batRunsAdded;
  }

  inn.totalRuns += totalAdded;
  striker.runs += batRunsAdded;
  bowler.runs += bowlerRunsAdded;

  if (batRunsAdded === 4) striker.fours += 1;
  if (batRunsAdded === 6) striker.sixes += 1;
  
  const targetBatsmanName = opts.outBatsman || inn.currentStriker;
  const outBat = _getBatsman(inn, targetBatsmanName);
  
  outBat.out = true;
  outBat.outType = opts.type;
  outBat.outBowler = opts.type === "RunOut" ? null : bowler.name;
  outBat.outFielder = opts.fielder || null;
  
  inn.wickets += 1;
  
  let isLegalBall = isLegal;
  if (extraType === "wide" && !rebowlWide) isLegalBall = true;
  if (extraType === "nb" && !rebowlNoBall) isLegalBall = true;

  if (isLegalBall) {
      inn.balls += 1;
      if (extraType !== "wide") striker.balls += 1; // wides don't count as balls faced
      bowler.balls += 1;
  } else if (extraType === "nb") {
      striker.balls += 1; // no-ball still counts as a ball faced by the striker
  }

  const bowlerWicketTypes = ['Bowled', 'Caught', 'LBW', 'Stumped', 'Hit Wicket'];
  if (bowlerWicketTypes.includes(opts.type) && extraType !== "nb") bowler.wickets += 1;

  // The over/ball index must follow the ball actually consumed (isLegalBall),
  // not the raw isLegal flag — with rebowlWides/rebowlNoBalls disabled the two
  // diverge and the wicket lands on the first ball of the NEXT over.
  inn.ballLog.push({
    over: Math.floor((inn.balls - (isLegalBall ? 1 : 0)) / 6),
    ball: ((inn.balls - (isLegalBall ? 1 : 0)) % 6) + 1,
    runs: totalAdded,
    type: "wicket",
    wicketType: opts.type,
    runSource: (opts.runSource === "bye" || opts.runSource === "legbye") ? (opts.runSource === "bye" ? "b" : "lb") : null,
    striker: strikerBefore,           // who was ON STRIKE for this delivery
    outBatsman: targetBatsmanName,    // who was actually dismissed (may be the non-striker)
    bowler: bowler.name,
    isLegal: isLegalBall,
    freeHit: wasFreeHit
  });

  if (isLegal) inn.freeHit = false;
  
  if (opts.type === "RunOut") {
    const changedEnds = (completedRuns + (opts.crossed ? 1 : 0)) % 2 === 1;
    inn.currentStriker = changedEnds ? nonStrikerBefore : strikerBefore;
    inn.currentNonStriker = changedEnds ? strikerBefore : nonStrikerBefore;
  }
  if (targetBatsmanName === inn.currentStriker) {
    inn.currentStriker = null;
  } else if (targetBatsmanName === inn.currentNonStriker) {
    inn.currentNonStriker = null;
  }

  const battingPlayers = inn.battingTeam === "A" ? match.teamA.players : match.teamB.players;
  const isLastMan = inn.wickets === battingPlayers.length - 1;

  if (match.lms && isLastMan && !inn.currentStriker && inn.currentNonStriker) {
    inn.currentStriker = inn.currentNonStriker;
    inn.currentNonStriker = null;
  }

  // ICC Law 18.11 (effective Oct 2022): after a catch the INCOMING batter always
  // takes strike, regardless of whether the batters crossed. End-of-over strike
  // rotation is handled separately by endOver() → _swapStrikers(). The old
  // "promote the survivor when they crossed" branch was removed here.

  if (opts.type !== "RunOut" && completedRuns % 2 === 1) {
    _swapStrikers(inn);
  }
}

export function setNewBatsman(match, name) {
  const inn = currentInnings(match);
  // If striker slot is empty, fill striker (mid-over wicket). Otherwise fill non-striker
  // (end-of-over wicket case where _swapStrikers already promoted the survivor).
  if (inn.currentStriker === null) inn.currentStriker = name;
  else if (inn.currentNonStriker === null) inn.currentNonStriker = name;
  else inn.currentStriker = name; // fallback (shouldn't happen)
}

export function isOverComplete(match) {
  const inn = currentInnings(match);
  if (inn.balls > 0 && inn.balls % 6 === 0) {
    const lastBall = inn.ballLog[inn.ballLog.length - 1];
    return lastBall && lastBall.isLegal;
  }
  return false;
}

export function isAwaitingNewBowler(match) {
  if (!match || !isOverComplete(match)) return false;
  const inn = currentInnings(match);
  const lastBall = inn.ballLog[inn.ballLog.length - 1];
  return !!lastBall?.bowler && lastBall.bowler === inn.currentBowler;
}

export function endOver(match, newBowlerName) {
  const inn = currentInnings(match);
  _swapStrikers(inn);
  inn.currentBowler = newBowlerName;
}

export function isInningsOver(match) {
  const inn = currentInnings(match);
  
  if (match.mode === 'challenge' && match.challenge && match.challenge.type !== 'super_over') {
    const c = match.challenge;
    if (c.type === 'target_runs') {
      if (inn.totalRuns >= c.targetValue) return true;
      if (inn.wickets > 0) return true;
    }
    if (c.type === 'target_sixes') {
      const totalSixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
      if (totalSixes >= c.targetValue) return true;
      if (inn.wickets > 0) return true;
    }
    if (c.type === 'target_wickets' && inn.wickets >= c.targetValue) return true;
  }

  if (inn.balls >= match.overs * 6) return true;
  const battingPlayers = inn.battingTeam === "A" ? match.teamA.players : match.teamB.players;
  const maxWickets = match.lms ? battingPlayers.length : battingPlayers.length - 1;
  if (inn.wickets >= maxWickets) return true;
  if (match.innings.length >= 2 && match.innings.length % 2 === 0) {
    const prevInn = match.innings[match.innings.length - 2];
    if (inn.totalRuns > prevInn.totalRuns) return true;
  }
  return false;
}

export function startSecondInnings(match) {
  // Guard against a double-tap on a laggy device pushing a 3rd innings into a
  // two-innings match — validateMatchSnapshot rejects innings.length > 2, so
  // that would make the match unloadable on the next boot.
  if (!match || !Array.isArray(match.innings) || match.innings.length >= 2) return;
  const firstInn = match.innings[match.innings.length - 1];
  const newBattingTeam = firstInn.battingTeam === "A" ? "B" : "A";
  const battingPlayers = newBattingTeam === "A" ? match.teamA.players : match.teamB.players;
  const bowlingPlayers = newBattingTeam === "A" ? match.teamB.players : match.teamA.players;
  match.innings.push(makeInnings(newBattingTeam, battingPlayers, bowlingPlayers));
  _resetUndo(match);
}

export function computeResult(match) {
  if (match.mode === "challenge" && match.challenge && match.challenge.type !== "super_over") {
    if (match.innings.length === 1 && isInningsOver(match)) {
      const inn = match.innings[0];
      const c = match.challenge;
      let won = false;
      const bowlingTeam = inn.battingTeam === "A" ? "B" : "A";
      if (c.type === 'target_runs') won = inn.totalRuns >= c.targetValue;
      else if (c.type === 'target_sixes') won = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0) >= c.targetValue;
      else if (c.type === 'target_wickets') return { winner: inn.wickets >= c.targetValue ? bowlingTeam : inn.battingTeam, margin: 0, marginType: "challenge" };
      
      return { winner: won ? inn.battingTeam : bowlingTeam, margin: 0, marginType: "challenge" };
    }
  }

  if (match.mode === "single" && match.innings.length === 1 && isInningsOver(match)) {
    return { winner: "COMPLETED", margin: 0, marginType: "completed" };
  }
  if (match.innings.length < 2) return null;
  const inn1 = match.innings[match.innings.length - 2], inn2 = match.innings[match.innings.length - 1];
  const isTargetChased = inn2.totalRuns > inn1.totalRuns;
  const isSecondInningsFinished = isInningsOver(match);
  
  if (!isTargetChased && !isSecondInningsFinished) return null;

  if (isTargetChased) {
    const totalPlayers = inn2.battingTeam === "A" ? match.teamA.players.length : match.teamB.players.length;
    const maxWickets = match.lms ? totalPlayers : Math.max(totalPlayers - 1, 1);
    const wicketsLeft = maxWickets - inn2.wickets;
    return { winner: inn2.battingTeam, margin: wicketsLeft, marginType: "wickets" };
  }
  if (inn2.totalRuns < inn1.totalRuns) {
    return { winner: inn1.battingTeam, margin: inn1.totalRuns - inn2.totalRuns, marginType: "runs" };
  }
  return { winner: "TIE", margin: 0, marginType: "tie" };
}

export function makeInnings(battingTeam, battingPlayers, bowlingPlayers) {
  return {
    battingTeam,
    batsmen: battingPlayers.map(name => ({ name, runs: 0, balls: 0, fours: 0, sixes: 0, out: false, outType: null, outBowler: null, outFielder: null })),
    bowlers: bowlingPlayers.map(name => ({ name, balls: 0, runs: 0, wickets: 0, maidens: 0 })),
    totalRuns: 0, wickets: 0, balls: 0,
    extras: { wide: 0, nb: 0, b: 0, lb: 0 },
    ballLog: [],
    currentStriker: null,
    currentNonStriker: null,
    currentBowler: null,
    freeHit: false
  };
}

export function currentOverNumber(inn) {
  return Math.floor(inn.balls / 6);
}

export function currentOverBalls(inn, overOverride) {
  const cur = overOverride !== undefined ? overOverride : currentOverNumber(inn);
  return inn.ballLog.filter(b => b.over === cur);
}

export function currentOverSummary(inn, overOverride) {
  const balls = currentOverBalls(inn, overOverride);
  let runs = 0, legal = 0, dots = 0, ones = 0, twos = 0, threes = 0, fours = 0, sixes = 0, wides = 0, nbs = 0, wickets = 0;
  for (const b of balls) {
    runs += b.runs;
    if (b.isLegal) legal += 1;
    if (b.type === "wide") wides += 1;
    if (b.type === "nb") nbs += 1;
    if (b.type === "wicket") wickets += 1;
    if (b.type === "dot") dots += 1;
    if (b.type === "run") {
      if (b.runs === 1) ones += 1;
      else if (b.runs === 2) twos += 1;
      else if (b.runs === 3) threes += 1;
      else if (b.runs === 4) fours += 1;
      else if (b.runs === 6) sixes += 1;
    }
  }
  return { balls, runs, legal, dots, ones, twos, threes, fours, sixes, wides, nbs, wickets };
}


export function formatOvers(legalBalls) {
  return Math.floor(legalBalls / 6) + "." + (legalBalls % 6);
}

export function isMatchComplete(match) {
  if (match.mode === "single" || (match.mode === "challenge" && match.challenge?.type !== "super_over")) {
    return match.innings.length >= 1 && isInningsOver(match);
  }
  return match.innings.length >= 2 && isInningsOver(match);
}
